"use client";

import { Spinner } from "@/components";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useHexStatsRefresh, useHexStatsStatus } from "@/hooks/use-hexstats";
import { cn } from "@/utils";

type DataRefreshButtonProps = {
  iconOnly?: boolean;
  className?: string;
};

export function DataRefreshButton({
  iconOnly = false,
  className,
}: DataRefreshButtonProps) {
  const { data: status } = useHexStatsStatus();
  const { refresh, loading } = useHexStatsRefresh();

  const dayLabel =
    status?.lastPulsechainDay != null
      ? `Day ${status.lastPulsechainDay}`
      : null;

  const title = status?.chartsReady
    ? `Charts through ${dayLabel ?? "latest day"}. Click to sync new days.`
    : "Sync latest chart data";

  if (iconOnly) {
    return (
      <button
        type="button"
        onClick={refresh}
        disabled={loading}
        aria-label="Refresh chart data"
        title={title}
        className={cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-surface-400/40 text-muted transition-colors hover:text-hex-400 disabled:opacity-50",
          className,
        )}
      >
        {loading ? (
          <Spinner className="h-4 w-4" />
        ) : (
          <ArrowPathIcon className="h-4 w-4 stroke-2" />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={refresh}
      disabled={loading}
      aria-label="Refresh chart data"
      title={title}
      className={cn(
        "inline-flex items-center gap-2 rounded-lg border border-white/10 bg-surface-400/40 px-3 py-2 text-xs text-white transition-colors hover:border-white/20 disabled:opacity-50",
        className,
      )}
    >
      {loading ? <Spinner className="h-3.5 w-3.5" /> : null}
      {loading ? "Refreshing…" : "Refresh data"}
      {!loading && dayLabel ? (
        <span className="hidden text-white/50 sm:inline">({dayLabel})</span>
      ) : null}
    </button>
  );
}

export default DataRefreshButton;
